import { useContext, useEffect, useMemo, useState } from "react"
import useSWR from "swr"
import dayjs from "dayjs"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { ToastDataContext } from "../context/ToastContextProvider"
import { UserDataContext } from "../context/UserContextProvider"
import {
    hrGetWaitingListAmountEndpoint,
    hrRegisterWaitingListEndpoint,
    hrRemoveWaitingListEndpoint,
} from "../hr_endpoints"
import HRWaitingListModal from "./HRWaitingListModal"

// The HR dashboard is a flat table of the day's slots, with no header per list.
// Anything that belongs to a list rather than a slot — the assigned therapists,
// the waiting list for a full list — lives in this strip above the table, so
// the table itself stays exactly as HR knows it.

function WaitingListActions({ list, onChanged }) {

    const { t } = useTranslation()
    const toastContext = useContext(ToastDataContext)
    const userContext = useContext(UserDataContext)
    const navigate = useNavigate()

    const { data: amount, error, mutate } = useSWR([hrGetWaitingListAmountEndpoint(list.id)])

    useEffect(() => {
        if (error) toastContext.error(error.message)
    }, [error])

    const call = async (endpoint, successKey) => {
        const response = await fetch(endpoint(list.id), {
            method: "POST",
            headers: {
                Authorization: `Bearer ${userContext.user?.token}`,
                "Content-Type": "application/json",
            },
        })

        if (response.status === 401) {
            userContext.setUserData({})
            navigate("/login")
            toastContext.error(t("sessionExpired"))
            return
        }

        if (!response.ok) {
            toastContext.error(await response.text())
        } else {
            toastContext.success(t(successKey))
        }

        mutate()
        onChanged()
    }

    return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <p className="text-sm text-neutral-500">
                {list.is_waiting_list
                    ? t("dashboard.waitingListRegisteredHint", { amount: amount ?? 0 })
                    : t("dashboard.waitingListHint", { amount: amount ?? 0 })}
            </p>
            {list.is_waiting_list ? (
                <button
                    type="button"
                    className="btn btn-sm bg-tertiary text-white hover:bg-tertiary/80 whitespace-nowrap"
                    onClick={() => call(hrRemoveWaitingListEndpoint, "dashboard.waitingListUnregistered")}
                >
                    {t("dashboard.unregisterFromWaitingList")}
                </button>
            ) : (
                <button
                    type="button"
                    className="btn btn-sm bg-secondary text-white hover:bg-secondary/80 whitespace-nowrap"
                    onClick={() => call(hrRegisterWaitingListEndpoint, "dashboard.waitingListRegistered")}
                >
                    {t("dashboard.registerOnWaitingList")}
                </button>
            )}
        </div>
    )
}

function ListStrip({ list, onChanged }) {

    const { t } = useTranslation()
    const [waitingListOpen, setWaitingListOpen] = useState(false)

    return (
        <div className="rounded-lg border border-neutral-border bg-base-100 px-4 py-3">
            <div className="flex flex-col gap-2">
                <div className="flex flex-col sm:flex-row sm:items-baseline sm:flex-wrap sm:gap-x-2">
                    <span className="font-semibold text-neutral-1100 whitespace-nowrap">
                        {dayjs(list.start_date).format("DD-MM-YYYY")}
                        {list.contract_type && (
                            <>
                                {" - "}
                                <span className="text-primary-dark">{list.contract_type}</span>
                            </>
                        )}
                    </span>
                    {list.showTherapists && (
                        <span className="text-sm text-neutral-500">
                            {t("dashboard.assignedTherapists")}
                        </span>
                    )}
                </div>

                {list.room_info && (
                    <p className="text-sm text-neutral-500">
                        {t("dashboard.roomInfo")}:{" "}
                        <span className="text-neutral-1100">{list.room_info}</span>
                    </p>
                )}

                {list.showTherapists && (
                    <div className="flex flex-wrap gap-2">
                        {list.therapists.map(therapist => (
                            <span
                                key={therapist}
                                className="badge badge-outline border-primary-dark text-primary-dark whitespace-nowrap"
                            >
                                {therapist}
                            </span>
                        ))}
                    </div>
                )}

                {(list.showWaitingList || list.showWaitingListView) && (
                    <div className="border-t border-neutral-border pt-2 mt-1">
                        {/* The date is already in the strip header above. */}
                        {list.showWaitingList && (
                            <p className="font-medium text-secondary mb-1">
                                {t("dashboard.waitingListFull")}
                            </p>
                        )}

                        <div className="flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-2 sm:gap-4">
                            {/* Joining is only offered while the list is actually
                                full; viewing who is queued is useful either way,
                                including after a slot frees up again. */}
                            {list.showWaitingList && (
                                <WaitingListActions list={list} onChanged={onChanged} />
                            )}

                            {list.showWaitingListView && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline border-primary-dark text-primary-dark hover:bg-primary-dark hover:text-white whitespace-nowrap"
                                    onClick={() => setWaitingListOpen(true)}
                                >
                                    {t("dashboard.viewWaitingList", { amount: list.waiting_list_count })}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <HRWaitingListModal
                open={waitingListOpen}
                setOpen={setWaitingListOpen}
                listId={list.id}
                listDate={list.start_date}
            />
        </div>
    )
}

// Collapses the day's slot rows back down to their lists — list-level state is
// repeated on every row of a list, so the first row of each wins.
export default function HRDashboardListInfo({ slots, onChanged }) {

    const lists = useMemo(() => {
        const byList = new Map()

        for (const slot of slots || []) {
            if (byList.has(slot.list_id)) continue

            const therapists = slot.list_therapists || []
            const showTherapists = !!slot.show_assigned_therapists && therapists.length > 0
            const showWaitingList = !!slot.list_is_full && !!slot.waiting_list_enabled
            // Seeing who is queued does not depend on the list still being full
            // — a slot freeing up is exactly when HR wants to look.
            const showWaitingListView = (slot.waiting_list_count || 0) > 0

            byList.set(slot.list_id, {
                id: slot.list_id,
                start_date: slot.list_start_date,
                contract_type: slot.contract_type,
                room_info: slot.room_info,
                is_waiting_list: slot.is_waiting_list,
                waiting_list_count: slot.waiting_list_count || 0,
                therapists,
                showTherapists,
                showWaitingList,
                showWaitingListView,
            })
        }

        return [...byList.values()]
    }, [slots])

    if (lists.length === 0) return null

    return (
        <div className="flex flex-col gap-2">
            {lists.map(list => (
                <ListStrip key={list.id} list={list} onChanged={onChanged} />
            ))}
        </div>
    )
}
